// P5 (2026-11-02) — Fan-out composer.
//
// Pick an approved asset set, tick a grid of (channel × surface) cells,
// and submit once. The backend creates N sibling plans under one
// fan_out_id. Each plan carries its own preflight, schedule and
// receipt — a failure on one platform never blocks the others.
//
// Not wired into the existing single-plan Compose flow on purpose:
// Roger's single-plan + single-schedule flow is in production and we
// don't want to perturb it with a mode toggle. This page is additive.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { channelDisplay } from "../lib/tz";

const DEFAULT_SURFACES_BY_CHANNEL = {
  facebook:  ["feed", "story", "reel", "carousel", "video_story"],
  instagram: ["feed", "story", "reel", "carousel", "video_story"],
  pinterest: ["pin", "video_pin", "carousel"],
  tiktok:    ["feed", "photo"],
  youtube:   ["short", "long", "custom_thumbnail"],
  email:     ["feed"],
};

// P5.4 (2026-11-03) · Item 1 — Map a set's ``kind`` field to the list
// of adapter ``kinds`` that set is compatible with. A feed set is
// image-based + has caption copy, so it fits any surface that
// accepts image OR text. A reel set is video, carousel is images.
const SET_KIND_TO_MEDIA_KINDS = {
  feed:     ["image", "text"],
  reel:     ["video"],
  video:    ["video"],
  carousel: ["image"],
  photo:    ["image"],
  story:    ["image", "video"],
  unknown:  ["image", "video", "text"],
};

export default function PublishFanOut() {
  const navigate = useNavigate();
  const [sets, setSets] = useState([]);
  const [surfaceSchemas, setSurfaceSchemas] = useState({});
  const [assetSetId, setAssetSetId] = useState("");
  const [grid, setGrid] = useState({});
  const [cfgByCell, setCfgByCell] = useState({});
  const [isAcceptanceTest, setIsAcceptanceTest] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [r_sets, r_schemas] = await Promise.all([
        api.get("/asset-sets/publishable?limit=100"),
        api.get("/publish/surface-schemas"),
      ]);
      setSets(r_sets.data?.asset_sets || []);
      setSurfaceSchemas(r_schemas.data?.surfaces || {});
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedSet = useMemo(
    () => sets.find((s) => s.id === assetSetId) || null,
    [sets, assetSetId],
  );

  function toggleCell(channel, surface) {
    const k = `${channel}:${surface}`;
    setGrid((g) => ({ ...g, [k]: !g[k] }));
  }

  function patchCfg(channel, surface, patch) {
    const k = `${channel}:${surface}`;
    setCfgByCell((m) => ({ ...m, [k]: { ...(m[k] || {}), ...patch } }));
  }

  // Decide whether a (channel × surface) cell is enabled based on the
  // set's media kind + the surface-schema rules.
  function cellStatus(channel, surface) {
    const key = `${channel}:${surface}`;
    const schema = surfaceSchemas[key];
    if (!schema) return { enabled: false, reason: "surface not supported" };
    if (!selectedSet) return { enabled: false, reason: "pick a set first" };
    // P5.4 (2026-11-03) · Item 1 — translate the SET kind (feed/reel/
    // carousel) into the MEDIA kinds (image/video/text) the adapter
    // actually gates on. Previously we compared "feed" directly to
    // ["image", "video", "text"] and gated out every cell.
    const setKind = (
      selectedSet.media_kind || selectedSet.kind || "unknown"
    ).toLowerCase();
    const setMediaKinds = (selectedSet.media_kinds
      || SET_KIND_TO_MEDIA_KINDS[setKind]
      || SET_KIND_TO_MEDIA_KINDS.unknown);
    const allowed = schema.kinds || [];
    const match = setMediaKinds.find((k) => allowed.includes(k));
    if (!match) {
      return {
        enabled: false,
        reason: `set is ${setMediaKinds.join("/")} · surface wants ${allowed.join("/")}`,
      };
    }
    return { enabled: true, reason: "" };
  }

  const selectedCells = useMemo(() => {
    return Object.entries(grid)
      .filter(([, v]) => v)
      .map(([k]) => {
        const [channel, surface] = k.split(":");
        return {
          channel, surface,
          channel_config: cfgByCell[k] || {},
        };
      });
  }, [grid, cfgByCell]);

  async function onSubmit() {
    setErr(null);
    if (!assetSetId) { setErr("Pick an approved set."); return; }
    if (selectedCells.length === 0) {
      setErr("Tick at least one (channel × surface) cell.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/publish/fan-out", {
        asset_set_id: assetSetId,
        cells: selectedCells,
        is_acceptance_test: isAcceptanceTest,
      });
      const fanOutId = r.data?.fan_out_id;
      if (fanOutId) navigate(`/brand-os/publish/fan-out/${fanOutId}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const CHANNELS = Object.keys(DEFAULT_SURFACES_BY_CHANNEL);

  return (
    <Shell title="Publish — Fan-out">
      <div className="bo-page" data-testid="fan-out-page" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="bo-eyebrow">Publish Infrastructure</div>
            <h1 className="bo-serif" style={{ fontSize: 26, margin: 0 }}>
              Fan-out composer
            </h1>
            <p style={{ color: "var(--bo-muted)", marginTop: 4, maxWidth: 640 }}>
              Pick an approved set, tick the surfaces, click once. One sibling
              plan per cell, each with its own schedule and receipt.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost"
                  data-testid="fan-out-queue-link"
                  style={{ textDecoration: "none" }}>
              ← Queue
            </Link>
          </div>
        </div>

        {err && (
          <div className="bo-card" data-testid="fan-out-error"
               style={{ padding: 12, color: "var(--bo-danger)", marginBottom: 12 }}>
            {typeof err === "string" ? err : err.message || JSON.stringify(err)}
          </div>
        )}
        {loading && (
          <div className="bo-card" style={{ padding: 16 }}>Loading…</div>
        )}

        {!loading && (
          <>
            <div className="bo-card" style={{ padding: 16, marginBottom: 16, display: "grid", gap: 10 }}>
              <label className="bo-label">Approved set</label>
              <select
                className="bo-input"
                data-testid="fan-out-set-select"
                value={assetSetId}
                onChange={(e) => setAssetSetId(e.target.value)}
              >
                <option value="">— pick an approved set —</option>
                {sets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {(s.name || s.title || s.id)} · {s.media_kind || s.kind || "?"}
                  </option>
                ))}
              </select>
              <label style={{ display: "flex", gap: 8, alignItems: "center",
                              fontSize: 13 }}>
                <input
                  type="checkbox"
                  data-testid="fan-out-acceptance-test"
                  checked={isAcceptanceTest}
                  onChange={(e) => setIsAcceptanceTest(e.target.checked)}
                />
                Mark as acceptance test (recommended during posting hold)
              </label>
            </div>

            {assetSetId && (
            <div className="bo-card" style={{ padding: 16, marginBottom: 16, overflowX: "auto" }}>
              <div className="bo-eyebrow" style={{ marginBottom: 8 }}>Channel × surface</div>
              <table data-testid="fan-out-grid"
                     style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8 }}>Channel</th>
                    {["feed", "story", "reel", "carousel", "pin", "video_pin", "photo"].map((sfc) => (
                      <th key={sfc} style={{ textAlign: "center", padding: 8,
                                             textTransform: "capitalize" }}>
                        {sfc.replace("_", " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CHANNELS.map((channel) => (
                    <tr key={channel}
                        style={{ borderTop: "1px solid var(--bo-line)" }}>
                      <td style={{ padding: 8, fontWeight: 600 }}>
                        {channelDisplay(channel)}
                      </td>
                      {["feed", "story", "reel", "carousel", "pin", "video_pin", "photo"].map((sfc) => {
                        const schemaKey = `${channel}:${sfc}`;
                        const schemaExists = !!surfaceSchemas[schemaKey];
                        if (!schemaExists) {
                          return <td key={sfc} style={{ padding: 8, textAlign: "center",
                                                        color: "var(--bo-muted-faint, #ccc)" }}>—</td>;
                        }
                        const { enabled, reason } = cellStatus(channel, sfc);
                        const k = `${channel}:${sfc}`;
                        const checked = !!grid[k];
                        return (
                          <td key={sfc} style={{ padding: 8, textAlign: "center" }}
                              title={!enabled ? reason : ""}>
                            <input
                              type="checkbox"
                              data-testid={`fan-out-cell-${channel}-${sfc}`}
                              disabled={!enabled}
                              checked={checked}
                              onChange={() => toggleCell(channel, sfc)}
                            />
                            {!enabled && (
                              <div style={{ fontSize: 10, color: "var(--bo-muted)" }}>
                                {reason}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

            {selectedCells.length > 0 && (
              <div className="bo-card" style={{ padding: 16, marginBottom: 16, display: "grid", gap: 12 }}>
                <div className="bo-eyebrow">Per-surface fields</div>
                {selectedCells.map((c) => {
                  const schemaKey = `${c.channel}:${c.surface}`;
                  const schema = surfaceSchemas[schemaKey];
                  const captionMax = schema?.text?.caption_max || 0;
                  const k = `${c.channel}:${c.surface}`;
                  const cfg = cfgByCell[k] || {};
                  return (
                    <div key={k} data-testid={`fan-out-cell-fields-${c.channel}-${c.surface}`}
                         style={{ border: "1px dashed var(--bo-line)", borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        {channelDisplay(c.channel)} · {c.surface.replace("_", " ")}
                      </div>
                      <label className="bo-label">Caption</label>
                      <textarea
                        className="bo-input"
                        data-testid={`fan-out-cell-caption-${c.channel}-${c.surface}`}
                        value={cfg.caption || ""}
                        onChange={(e) => patchCfg(c.channel, c.surface,
                                                   { caption: e.target.value })}
                        rows={3}
                        style={{ width: "100%" }}
                      />
                      <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>
                        {(cfg.caption || "").length}{captionMax ? ` / ${captionMax}` : ""}
                      </div>
                      {schema?.text?.requires_board_id && (
                        <div style={{ marginTop: 8, fontSize: 12,
                                      color: "var(--bo-muted)" }}>
                          This surface requires a board_id (set on the plan after
                          create via the single-plan editor).
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="bo-btn"
                data-testid="fan-out-submit"
                disabled={busy || selectedCells.length === 0 || !assetSetId}
                onClick={onSubmit}
              >
                {busy ? "Creating…"
                     : `Create ${selectedCells.length} sibling plan${selectedCells.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
